import { useEffect, useRef } from 'react'
import { authClient } from './authClient'
import { useAuthStore } from './authStore'
import LoginPage from './LoginPage'

/**
 * Wraps the app shell. On boot, tries to mint a fresh access token from the
 * refresh cookie. If that succeeds we render the dashboard; otherwise we show
 * the login screen. Children only render once `status === "authenticated"`, so
 * the dashboard (and its WebSocket) cannot start unauthenticated.
 */
export default function AuthGate({ children }: { children: React.ReactNode }) {
  const status = useAuthStore((s) => s.status)
  const expiresAtMs = useAuthStore((s) => s.expiresAtMs)
  const setChecking = useAuthStore((s) => s.setChecking)
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated)
  const setAnonymous = useAuthStore((s) => s.setAnonymous)

  const didBoot = useRef(false)
  useEffect(() => {
    if (didBoot.current) return
    didBoot.current = true
    setChecking()
    void (async () => {
      try {
        const r = await authClient.refresh()
        setAuthenticated({
          accessToken: r.access_token,
          expiresIn: r.expires_in,
          user: r.user,
        })
      } catch {
        setAnonymous(null)
      }
    })()
  }, [setChecking, setAuthenticated, setAnonymous])

  // ── Background refresh loop ─────────────────────────────────────────────
  // Refresh ~60s before the access token expires so REST/WS calls never see
  // a 401 in normal use.
  useEffect(() => {
    if (status !== 'authenticated' || !expiresAtMs) return
    const refreshAt = expiresAtMs - 60_000
    const ms = Math.max(5_000, refreshAt - Date.now())
    const t = window.setTimeout(async () => {
      try {
        const r = await authClient.refresh()
        useAuthStore.getState().setAuthenticated({
          accessToken: r.access_token,
          expiresIn: r.expires_in,
          user: r.user,
        })
      } catch {
        useAuthStore.getState().setAnonymous('session_expired')
      }
    }, ms)
    return () => window.clearTimeout(t)
  }, [status, expiresAtMs])

  if (status === 'idle' || status === 'checking') {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg-0, #0b0d12)',
          color: 'var(--t-2, #b4bbcd)',
          fontSize: 13,
        }}
      >
        Checking session…
      </div>
    )
  }

  if (status === 'anonymous') return <LoginPage />

  return <>{children}</>
}
